import React, { useEffect, useState, useMemo } from 'react';
import { 
  Container, 
  Table, 
  TableHead, 
  TableBody, 
  TableCell, 
  TableRow, 
  TextField, 
  Grid, 
  Button, 
  Pagination, 
  CircularProgress, 
  Typography,
  Paper,
  Box,
  Alert,
  TableContainer,
  IconButton,
  Tooltip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  OutlinedInput
} from '@mui/material';
import { useSelector } from 'react-redux';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import apiService from '../../../actions/services';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import UpdateIcon from '@mui/icons-material/Update';
import SettingsIcon from '@mui/icons-material/Settings';
import FilterListIcon from '@mui/icons-material/FilterList';

// Clave para almacenar en localStorage
const STORAGE_KEYS = {
  ROWS_PER_PAGE: 'cobertura_dias_rows_per_page',
  CURRENT_PAGE: 'cobertura_dias_current_page',
  MODIFICATIONS: 'cobertura_dias_modifications'
};


const NotificationSystem = ({ notifications, onRemoveNotification }) => {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxWidth: 400,
          width: '100%'
        }}
      >
        {notifications.map((notification) => (
          <Box
            key={notification.id}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2,
              borderRadius: 1,
              boxShadow: 3,
              animation: 'slideIn 0.3s ease-out',
              position: 'relative',
              overflow: 'hidden',
              ...(notification.type === 'success' && {
                backgroundColor: '#d4edda',
                color: '#155724',
                border: '1px solid #c3e6cb'
              }),
              ...(notification.type === 'error' && {
                backgroundColor: '#f8d7da',
                color: '#721c24',
                border: '1px solid #f5c6cb'
              }),
              ...(notification.type === 'warning' && {
                backgroundColor: '#fff3cd',
                color: '#856404',
                border: '1px solid #ffeaa7'
              }),
              ...(notification.type === 'info' && {
                backgroundColor: '#d1ecf1',
                color: '#0c5460',
                border: '1px solid #bee5eb'
              })
            }}
          >
            {/* Icono */}
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 24 }}>
              {notification.type === 'success' && '✅'}
              {notification.type === 'error' && '❌'}
              {notification.type === 'warning' && '⚠️'}
              {notification.type === 'info' && 'ℹ️'}
            </Box>
            
            {/* Contenido */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {notification.title}
              </Typography>
              <Typography variant="body2">
                {notification.message}
              </Typography>
              {notification.details && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                  {notification.details}
                </Typography>
              )}
            </Box>
            
            {/* Botón cerrar */}
            <IconButton
              size="small"
              onClick={() => onRemoveNotification(notification.id)}
              sx={{ 
                ml: 1,
                alignSelf: 'flex-start',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' }
              }}
            >
              ×
            </IconButton>
            
            {/* Barra de progreso */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                height: 3,
                backgroundColor: 'rgba(0,0,0,0.1)',
                animation: 'progressBar 10s linear forwards',
                animationDelay: '0.3s'
              }}
            />
          </Box>
        ))}
        
        {/* Estilos CSS para animaciones */}
        <style>{`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes progressBar {
            from {
              width: 100%;
            }
            to {
              width: 0%;
            }
          }
        `}</style>
      </Box>
    );
  };
  
  function CargaCoberturaDias() {
      const { user } = useSelector(state => state.auth);
      const [loading, setLoading] = useState(false);
      const [error, setError] = useState(null);
      const [data, setData] = useState([]);
      const [originalData, setOriginalData] = useState([]);
      const [searchTerm, setSearchTerm] = useState('');
      const [page, setPage] = useState(() => {
        const savedPage = localStorage.getItem(STORAGE_KEYS.CURRENT_PAGE);
        return savedPage ? parseInt(savedPage) : 1;
      });
      const [rowsPerPage, setRowsPerPage] = useState(() => {
        const savedRows = localStorage.getItem(STORAGE_KEYS.ROWS_PER_PAGE);
        return savedRows ? parseInt(savedRows) : 100;
      });
      const [filterType, setFilterType] = useState('all');
      const [editingId, setEditingId] = useState(null);
      const [editRow, setEditRow] = useState({});
      const [modifications, setModifications] = useState(() => {
        const savedModifications = localStorage.getItem(STORAGE_KEYS.MODIFICATIONS);
        return savedModifications ? JSON.parse(savedModifications) : [];
      });
      const [showUpdateDialog, setShowUpdateDialog] = useState(false);
      
      // Estado para notificaciones
      const [notifications, setNotifications] = useState([]);
  
      // Definir columnas
      const columns = [
          { field: 'ITEMCODE', headerName: 'Código', width: 150, editable: false },
          { field: 'ItemName', headerName: 'Nombre', width: 150, editable: false },
          { field: 'TZ10', headerName: 'TZ10', width: 100, editable: true },
          { field: 'TPRR', headerName: 'TPRR', width: 100, editable: true },
          { field: 'TCSV', headerName: 'TCSV', width: 100, editable: true },
          { field: 'TSCR', headerName: 'TSCR', width: 100, editable: true },
          { field: 'TMIR', headerName: 'TMIR', width: 100, editable: true },
          { field: 'TXLA', headerName: 'TXLA', width: 100, editable: true },
      ];
  
      // Función para agregar notificaciones
      const addNotification = (type, title, message, details = null) => {
        const id = Date.now() + Math.random();
        const newNotification = {
          id,
          type,
          title,
          message,
          details,
          timestamp: new Date()
        };
  
        setNotifications(prev => [...prev, newNotification]);
  
        // Auto-remover después de 8 segundos
        setTimeout(() => {
          removeNotification(id);
        }, 8000);
      };
  
      // Función para remover notificaciones
      const removeNotification = (id) => {
        setNotifications(prev => prev.filter(notification => notification.id !== id));
      };
  
      const fetchData = async () => {
          try {
              setLoading(true);
              setError(null);
              const dat = { "Vista": "CoberturaDias" };
              const resp = await apiService.get("ListaReportesSalas", dat, user);
              
              if (resp.datos_Generados) {
                  const parsedData = JSON.parse(resp.datos_Generados);
                  console.log("Datos cargados:", parsedData);
                  setData(parsedData);
                  setOriginalData([...parsedData]);
                  
                  // Aplicar modificaciones guardadas a los datos
                  if (modifications.length > 0) {
                      applyModificationsToData(parsedData);
                  }
                  
                  // Mostrar notificación de éxito
                  addNotification(
                    'success',
                    'Datos cargados',
                    `Se cargaron ${parsedData.length} registros exitosamente`
                  );
              }
          } catch (error) {
              console.error(error);
              const errorMsg = error.message || "Error al cargar los datos";
              setError(errorMsg);
              
              // Mostrar notificación de error
              addNotification(
                'error',
                'Error al cargar',
                errorMsg
              );
          } finally {
              setLoading(false);
          }
      }
  
      // Aplicar modificaciones a los datos
      const applyModificationsToData = (dataArray) => {
          const updatedData = [...dataArray];
          modifications.forEach(mod => {
              const index = updatedData.findIndex(item => item.ITEMCODE === mod.ITEMCODE);
              if (index !== -1) {
                  Object.keys(mod.changes).forEach(field => {
                      if (field in updatedData[index]) {
                          updatedData[index][field] = mod.changes[field].newValue;
                      }
                  });
              }
          });
          setData(updatedData);
      };
  
      useEffect(() => {
          fetchData();
      }, [user?.UserNickName]);
  
      // Guardar preferencias en localStorage
      useEffect(() => {
          localStorage.setItem(STORAGE_KEYS.ROWS_PER_PAGE, rowsPerPage.toString());
      }, [rowsPerPage]);
  
      useEffect(() => {
          localStorage.setItem(STORAGE_KEYS.CURRENT_PAGE, page.toString());
      }, [page]);
  
      useEffect(() => {
          localStorage.setItem(STORAGE_KEYS.MODIFICATIONS, JSON.stringify(modifications));
      }, [modifications]);
  
      // Filtrar datos con búsqueda en ITEMCODE y ItemName
      const filteredData = useMemo(() => {
          let filtered = data;
  
          if (filterType === 'PTAJ') {
              filtered = filtered.filter(item => item.ITEMCODE && item.ITEMCODE.startsWith('PTAJ'));
          } else if (filterType === 'PTPR') {
              filtered = filtered.filter(item => item.ITEMCODE && item.ITEMCODE.startsWith('PTPR'));
          }
  
          if (searchTerm) {
              const term = searchTerm.toLowerCase().trim();
              filtered = filtered.filter(item => {
                  const itemCodeMatch = item.ITEMCODE && 
                      item.ITEMCODE.toLowerCase().includes(term);
                  
                  const itemNameMatch = item.ItemName && 
                      item.ItemName.toLowerCase().includes(term);
                  
                  return itemCodeMatch || itemNameMatch;
              });
          }
  
          return filtered;
      }, [data, searchTerm, filterType]);
  
      // Calcular datos paginados
      const paginatedData = useMemo(() => {
          const startIndex = (page - 1) * rowsPerPage;
          return filteredData.slice(startIndex, startIndex + rowsPerPage);
      }, [filteredData, page, rowsPerPage]);
  
      // Calcular total de páginas
      const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  
      const handlePageChange = (event, value) => {
          setPage(value);
      };
  
      const handleSearchChange = (event) => {
          setSearchTerm(event.target.value);
          setPage(1);
      };
  
      const handleFilterChange = (event) => {
          setFilterType(event.target.value);
          setPage(1);
      };
  
      const handleRowsPerPageChange = (event) => {
          const newRowsPerPage = parseInt(event.target.value);
          setRowsPerPage(newRowsPerPage);
          setPage(1);
      };
  
      // Funciones para edición
      const handleEditClick = (row) => {
          setEditingId(row.ITEMCODE);
          setEditRow({ ...row });
      };
  
      const handleSaveClick = () => {
          if (!editingId) return;
  
          // Crear objeto con los cambios
          const changes = {};
          const updatedData = [...data];
          const dataIndex = updatedData.findIndex(item => item.ITEMCODE === editingId);
          const originalIndex = originalData.findIndex(item => item.ITEMCODE === editingId);
          
          if (dataIndex !== -1 && originalIndex !== -1) {
              // Guardar cambios comparando con datos originales
              columns.forEach(column => {
                  if (column.editable && column.field !== 'ITEMCODE') {
                      const field = column.field;
                      const originalValue = originalData[originalIndex][field] || 0;
                      const newValue = editRow[field] || 0;
                      
                      if (originalValue !== newValue) {
                          changes[field] = {
                              oldValue: originalValue,
                              newValue: newValue
                          };
                          updatedData[dataIndex][field] = newValue;
                      }
                  }
              });
  
              // Actualizar datos
              setData(updatedData);
  
              // Actualizar modificaciones
              if (Object.keys(changes).length > 0) {
                  const existingModIndex = modifications.findIndex(m => m.ITEMCODE === editingId);
                  
                  if (existingModIndex !== -1) {
                      const updatedModifications = [...modifications];
                      updatedModifications[existingModIndex] = {
                          ITEMCODE: editingId,
                          changes: { 
                              ...updatedModifications[existingModIndex].changes,
                              ...changes 
                          },
                          timestamp: new Date().toISOString()
                      };
                      setModifications(updatedModifications);
                  } else {
                      setModifications(prev => [...prev, {
                          ITEMCODE: editingId,
                          changes: changes,
                          timestamp: new Date().toISOString()
                      }]);
                  }
                  
                  // Mostrar notificación de éxito
                  addNotification(
                    'info',
                    'Cambios guardados',
                    `Cobertura de  ${editingId} listo para actualizarce`,
                    `Se modificaron ${Object.keys(changes).length} campos`
                  );
              } else {
                  // Mostrar notificación de información si no hay cambios
                  addNotification(
                    'warning',
                    'Sin cambios',
                    `No se realizaron cambios en ${editingId}`
                  );
              }
          }
  
          setEditingId(null);
          setEditRow({});
      };
  
      const handleCancelClick = () => {
          setEditingId(null);
          setEditRow({});
          addNotification('warning', 'Edición cancelada', 'Los cambios no se guardaron');
      };
  
      const handleEditChange = (field, value) => {
          setEditRow(prev => ({
              ...prev,
              [field]: parseInt(value) || 0
          }));
      };
  
      // Actualizar todas las modificaciones
      const handleUpdateModifications = () => {
          setShowUpdateDialog(true);
      };
  
      const confirmUpdateModifications = async () => {
          try {
              setLoading(true);
              
              // Preparar datos para enviar en el formato que espera el SP
              const updateData = modifications.map(mod => ({
                  ITEMCODE: mod.ITEMCODE,
                  cambios: mod.changes
              }));
  
              console.log("Datos a actualizar (formato SP):", updateData);
              
              // Enviar los datos como JSON string
              const response = await apiService.post("ActualizarCoberturasSalas", updateData, user);
              
              console.log("Respuesta completa del servidor:", response);
              
              // Procesar la respuesta - el JSON viene en datos_Generados
              let result;
              if (response.datos_Generados) {
                  try {
                      result = JSON.parse(response.datos_Generados);
                      console.log("JSON parseado de datos_Generados:", result);
                  } catch (parseError) {
                      console.error("Error al parsear datos_Generados:", parseError);
                      // Si no se puede parsear, usar la respuesta completa
                      result = response;
                  }
              } else if (response.jsonResultado) {
                  // Si viene directamente en jsonResultado
                  result = typeof response.jsonResultado === 'string' 
                      ? JSON.parse(response.jsonResultado) 
                      : response.jsonResultado;
              } else {
                  // Usar la respuesta directa
                  result = response;
              }
              
              console.log("Resultado procesado:", result);
              
              // Verificar respuesta basada en el formato del SP
              if (result && result.resultado === 'OK') {
                  // Mostrar notificación de éxito con detalles del SP
                  addNotification(
                    'success',
                    'Actualización exitosa',
                    result.mensaje || 'Datos actualizados correctamente',
                    `Registros afectados: ${result.registrosAfectados || 0} (Actualizaciones: ${result.actualizaciones || 0}, Inserciones: ${result.inserciones || 0})`
                  );
                  
                  // Limpiar modificaciones y actualizar datos
                  setModifications([]);
                  localStorage.removeItem(STORAGE_KEYS.MODIFICATIONS);
                  
                  // Recargar datos para reflejar cambios
                  await fetchData();
                  setShowUpdateDialog(false);
                  
              } else if (result && result.resultado === 'ERROR') {
                  // Mostrar notificación de error específico del SP
                  const errorDetails = result.detalleError 
                      ? `Error ${result.detalleError.errorNumber || 'desconocido'}: ${result.mensaje}`
                      : result.mensaje || 'Error en la operación';
                  
                  addNotification(
                    'error',
                    'Error en actualización',
                    errorDetails,
                    `Línea de error: ${result.detalleError?.errorLine || 'N/A'}`
                  );
                  
                  // Si hay info de debug, mostrarla en consola
                  if (result.debugInfo) {
                      console.error("Debug info del error:", result.debugInfo);
                  }
                  
              } else {
                  // Formato inesperado
                  addNotification(
                    'error',
                    'Error en respuesta',
                    'La respuesta del servidor tiene un formato inesperado',
                    JSON.stringify(result).substring(0, 200) + '...'
                  );
              }
  
          } catch (error) {
              console.error("Error al actualizar:", error);
              
              // Mostrar notificación de error
              addNotification(
                'error',
                'Error en actualización',
                'No se pudieron guardar los cambios en el servidor',
                error.message || 'Error de conexión'
              );
          } finally {
              setLoading(false);
          }
      };
  
      // Renderizar celda
      const renderCell = (row, column) => {
          // Si es la columna de acciones
          if (column.field === 'actions') {
              if (editingId === row.ITEMCODE) {
                  return (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Guardar">
                              <IconButton 
                                  size="small" 
                                  color="success"
                                  onClick={handleSaveClick}
                              >
                                  <SaveIcon fontSize="small" />
                              </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancelar">
                              <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={handleCancelClick}
                              >
                                  <CancelIcon fontSize="small" />
                              </IconButton>
                          </Tooltip>
                      </Box>
                  );
              }
              return (
                  <Tooltip title="Editar">
                      <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleEditClick(row)}
                      >
                          <EditIcon fontSize="small" />
                      </IconButton>
                  </Tooltip>
              );
          }
  
          // Si está en modo edición y es editable
          if (editingId === row.ITEMCODE && column.editable) {
              return (
                  <TextField
                      size="small"
                      type="number"
                      value={editRow[column.field] || 0}
                      onChange={(e) => handleEditChange(column.field, e.target.value)}
                      sx={{ width: '80px' }}
                      inputProps={{ 
                          min: 0,
                          style: { textAlign: 'center' }
                      }}
                  />
              );
          }
  
          // Valor normal
          return row[column.field] || '';
      };
  
      // Opciones de rows por página
      const rowsPerPageOptions = [100, 200, 300, 400, 500, 600, 700, 800, 900, 1000];
  
      return (
          <Container maxWidth="xl" sx={{ py: 4 }}>
              {/* Sistema de Notificaciones */}
              <NotificationSystem
                  notifications={notifications}
                  onRemoveNotification={removeNotification}
              />
  
              {/* Diálogo de confirmación */}
              <Dialog open={showUpdateDialog} onClose={() => setShowUpdateDialog(false)}>
                  <DialogTitle>Confirmar Actualización</DialogTitle>
                  <DialogContent>
                      <Typography>
                          ¿Estás seguro de que deseas actualizar {modifications.length} modificación(es)?
                      </Typography>
                      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
                          <Typography variant="body2" color="textSecondary">
                              Se enviarán los siguientes cambios:
                          </Typography>
                          <pre style={{ fontSize: '0.8rem', margin: 0 }}>
                              {JSON.stringify(modifications.map(m => ({
                                  ITEMCODE: m.ITEMCODE,
                                  cambios: m.changes
                              })), null, 2)}
                          </pre>
                      </Box>
                  </DialogContent>
                  <DialogActions>
                      <Button onClick={() => setShowUpdateDialog(false)}>Cancelar</Button>
                      <Button 
                          onClick={confirmUpdateModifications} 
                          variant="contained" 
                          color="primary"
                          disabled={loading}
                      >
                          {loading ? 'Actualizando...' : 'Confirmar'}
                      </Button>
                  </DialogActions>
              </Dialog>
  
              <h2>Coberturas por producto</h2>
  
              {/* Botón para actualizar modificaciones */}
              {modifications.length > 0 && (
                  <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Alert severity="info" sx={{ flex: 1, mr: 2 }}>
                          Tienes {modifications.length} modificación(es) pendientes de actualizar
                      </Alert>
                      <Tooltip title="Actualizar todas las modificaciones">
                          <Button
                              variant="contained"
                              color="primary"
                              startIcon={<UpdateIcon />}
                              onClick={handleUpdateModifications}
                              size="large"
                          >
                              Actualizar Modificaciones
                          </Button>
                      </Tooltip>
                  </Box>
              )}
  
              {/* Controles de búsqueda y filtros */}
              <Paper sx={{ p: 3, mb: 3 }}>
                  <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={5}>
                          <TextField
                              fullWidth
                              variant="outlined"
                              placeholder="Buscar por código o nombre de producto..."
                              value={searchTerm}
                              onChange={handleSearchChange}
                              InputProps={{
                                  startAdornment: (
                                      <InputAdornment position="start">
                                          <SearchIcon />
                                      </InputAdornment>
                                  ),
                              }}
                              helperText="Busca en código de producto y nombre"
                          />
                      </Grid>
                      <Grid item xs={12} md={2}>
                          <FormControl fullWidth>
                              <InputLabel id="filter-type-label">Tipo Producto</InputLabel>
                              <Select
                                  labelId="filter-type-label"
                                  value={filterType}
                                  onChange={handleFilterChange}
                                  label="Tipo Producto"
                                  startAdornment={
                                      <InputAdornment position="start">
                                          <FilterListIcon fontSize="small" />
                                      </InputAdornment>
                                  }
                              >
                                  <MenuItem value="all">Todos</MenuItem>
                                  <MenuItem value="PTAJ">PTAJ</MenuItem>
                                  <MenuItem value="PTPR">PTPR</MenuItem>
                              </Select>
                          </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                          <FormControl fullWidth>
                              <InputLabel id="rows-per-page-label">Registros por página</InputLabel>
                              <Select
                                  labelId="rows-per-page-label"
                                  value={rowsPerPage}
                                  onChange={handleRowsPerPageChange}
                                  label="Registros por página"
                                  startAdornment={
                                      <InputAdornment position="start">
                                          <SettingsIcon fontSize="small" />
                                      </InputAdornment>
                                  }
                              >
                                  {rowsPerPageOptions.map(option => (
                                      <MenuItem key={option} value={option}>
                                          {option} registros
                                      </MenuItem>
                                  ))}
                              </Select>
                          </FormControl>
                      </Grid>
                      <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                          <Typography variant="body2" color="textSecondary" align="right">
                              <strong>{filteredData.length}</strong> registros encontrados
                          </Typography>
                      </Grid>
                  </Grid>
                  
                  {/* Información adicional de búsqueda */}
                  {searchTerm && (
                      <Box sx={{ mt: 2, p: 1, backgroundColor: 'info.light', borderRadius: 1 }}>
                          <Typography variant="caption" color="white">
                              Buscando: "{searchTerm}" en código y nombre de productos
                          </Typography>
                      </Box>
                  )}
              </Paper>
  
              {/* Tabla de datos */}
              <TableContainer component={Paper} sx={{ mb: 3, maxHeight: '70vh' }}>
                  <Table size="small" stickyHeader>
                      <TableHead>
                          <TableRow>
                              {columns.map((column) => (
                                  <TableCell
                                      key={column.field}
                                      sx={{
                                          fontWeight: 'bold',
                                          backgroundColor: 'primary.main',
                                          color: 'primary.contrastText',
                                          position: 'sticky',
                                          top: 0,
                                          zIndex: 1
                                      }}
                                  >
                                      {column.headerName}
                                  </TableCell>
                              ))}
                              {/* Columna de acciones separada */}
                              <TableCell
                                  sx={{
                                      fontWeight: 'bold',
                                      backgroundColor: 'primary.main',
                                      color: 'primary.contrastText',
                                      position: 'sticky',
                                      top: 0,
                                      zIndex: 1
                                  }}
                              >
                                  Acciones
                              </TableCell>
                          </TableRow>
                      </TableHead>
                      <TableBody>
                          {loading ? (
                              <TableRow>
                                  <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 8 }}>
                                      <CircularProgress />
                                      <Typography sx={{ mt: 2 }}>Cargando datos...</Typography>
                                  </TableCell>
                              </TableRow>
                          ) : paginatedData.length > 0 ? (
                              paginatedData.map((row) => (
                                  <TableRow 
                                      key={row.ITEMCODE}
                                      hover
                                      sx={{
                                          backgroundColor: modifications.some(m => m.ITEMCODE === row.ITEMCODE) 
                                              ? 'rgba(255, 243, 205, 0.3)' 
                                              : 'inherit',
                                          '&:nth-of-type(odd)': {
                                              backgroundColor: modifications.some(m => m.ITEMCODE === row.ITEMCODE) 
                                                  ? 'rgba(255, 243, 205, 0.5)' 
                                                  : 'action.hover',
                                          },
                                      }}
                                  >
                                      {columns.map((column) => (
                                          <TableCell 
                                              key={`${row.ITEMCODE}-${column.field}`}
                                              sx={{ verticalAlign: 'middle' }}
                                          >
                                              {renderCell(row, column)}
                                          </TableCell>
                                      ))}
                                      {/* Celda de acciones */}
                                      <TableCell sx={{ verticalAlign: 'middle' }}>
                                          {renderCell(row, { field: 'actions' })}
                                      </TableCell>
                                  </TableRow>
                              ))
                          ) : (
                              <TableRow>
                                  <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 8 }}>
                                      <Typography variant="body1" color="textSecondary">
                                          {searchTerm 
                                              ? `No se encontraron resultados para "${searchTerm}"`
                                              : 'No se encontraron datos'}
                                      </Typography>
                                  </TableCell>
                              </TableRow>
                          )}
                      </TableBody>
                  </Table>
              </TableContainer>
  
              {/* Paginación */}
              {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                      <Box>
                          <Typography variant="body2" color="textSecondary">
                              Página {page} de {totalPages}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                              Mostrando {(page - 1) * rowsPerPage + 1} - {Math.min(page * rowsPerPage, filteredData.length)} de {filteredData.length} registros
                          </Typography>
                      </Box>
                      <Pagination
                          count={totalPages}
                          page={page}
                          onChange={handlePageChange}
                          color="primary"
                          showFirstButton
                          showLastButton
                          size="large"
                      />
                  </Box>
              )}
          </Container>
      );
  }
  
  export default CargaCoberturaDias;
